// src/pages/logistica/ExactitudConteo.jsx
import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';

const CATEGORIAS = {
  PT: ['Granola', 'Cereal', 'Barras', 'General'],
  MP: ['Desglose 1', 'Desglose 2', 'ME', '8 SKUs', 'General'],
};

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ExactitudConteo() {
  const [fecha, setFecha] = useState(hoyISO());
  const [tipo, setTipo] = useState('PT');
  const [categoria, setCategoria] = useState('');
  const [sugerencia, setSugerencia] = useState({ PT: null, MP: null });
  const [lotes, setLotes] = useState([]);
  const [conteos, setConteos] = useState({}); // { `${codigo_upc}-${numero_lote}`: { tarimas: [...], guardado: bool, correcto, diferencia } }
  const [cargando, setCargando] = useState(false);
  const [guardandoLote, setGuardandoLote] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [revelarNetSuite, setRevelarNetSuite] = useState(false); // conteo a ciegas por default

  // 1. Al cambiar la fecha, cargar la sugerencia del calendario para PT y MP
  useEffect(() => {
    api.get('/api/logistica/exactitud/calendario-sugerido', { params: { fecha } })
      .then((res) => {
        setSugerencia(res.data.data);
        setCategoria(res.data.data[tipo] || '');
      })
      .catch((err) => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  // 2. Al cambiar el tipo, aplicar la sugerencia correspondiente (si existe)
  useEffect(() => {
    setCategoria(sugerencia[tipo] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  // 3. Al tener tipo+categoría, cargar lotes + progreso ya guardado
  useEffect(() => {
    if (!categoria) { setLotes([]); return; }
    cargarLotesYProgreso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, categoria, fecha]);

  async function cargarLotesYProgreso() {
    try {
      setCargando(true);
      setMensaje(null);
      const [resLotes, resProgreso] = await Promise.all([
        api.get('/api/logistica/exactitud/lotes', { params: { tipo, categoria } }),
        api.get('/api/logistica/exactitud/progreso', { params: { tipo, categoria, fecha } }),
      ]);
      setLotes(resLotes.data.data);

      const mapaProgreso = {};
      resProgreso.data.data.forEach((c) => {
        mapaProgreso[`${c.codigo_upc}-${c.numero_lote}`] = {
          tarimas: c.tarimas || [],
          guardado: true,
          correcto: c.correcto,
          diferencia: parseFloat(c.diferencia),
          conteoReal: parseFloat(c.conteo_real),
        };
      });
      setConteos(mapaProgreso);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los lotes.' });
    } finally {
      setCargando(false);
    }
  }

  function claveLote(l) { return `${l.codigo_upc}-${l.numero_lote}`; }

  function getTarimas(l) {
    return conteos[claveLote(l)]?.tarimas?.length ? conteos[claveLote(l)].tarimas : [''];
  }

  function handleCambiarTarima(l, idx, valor) {
    const clave = claveLote(l);
    setConteos((prev) => {
      const actuales = prev[clave]?.tarimas?.length ? [...prev[clave].tarimas] : [''];
      actuales[idx] = valor;
      return { ...prev, [clave]: { ...prev[clave], tarimas: actuales, guardado: false } };
    });
  }

  function handleAgregarTarima(l) {
    const clave = claveLote(l);
    setConteos((prev) => {
      const actuales = prev[clave]?.tarimas?.length ? [...prev[clave].tarimas] : [''];
      return { ...prev, [clave]: { ...prev[clave], tarimas: [...actuales, ''], guardado: false } };
    });
  }

  function handleQuitarTarima(l, idx) {
    const clave = claveLote(l);
    setConteos((prev) => {
      const actuales = [...(prev[clave]?.tarimas || [''])];
      actuales.splice(idx, 1);
      return { ...prev, [clave]: { ...prev[clave], tarimas: actuales.length ? actuales : [''], guardado: false } };
    });
  }

  function sumaTarimas(l) {
    return getTarimas(l).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  }

  async function handleGuardarLote(l) {
    const clave = claveLote(l);
    const tarimas = getTarimas(l).filter((v) => v !== '').map((v) => parseFloat(v));
    if (tarimas.length === 0) {
      setMensaje({ tipo: 'error', texto: 'Captura al menos una tarima antes de guardar.' });
      return;
    }
    try {
      setGuardandoLote(clave);
      const res = await api.post('/api/logistica/exactitud/conteo', {
        tipo,
        categoria_auditada: categoria,
        fecha_auditoria: fecha,
        codigo_upc: l.codigo_upc,
        articulo: l.articulo,
        numero_lote: l.numero_lote,
        cantidad_netsuite: l.cantidad_netsuite,
        unidad: l.unidad,
        tarimas,
      });
      setConteos((prev) => ({
        ...prev,
        [clave]: { tarimas, guardado: true, correcto: res.data.correcto, diferencia: parseFloat(res.data.diferencia), conteoReal: parseFloat(res.data.conteo_real) },
      }));
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar el conteo.' });
    } finally {
      setGuardandoLote(null);
    }
  }

  const resumen = useMemo(() => {
    const contados = Object.values(conteos).filter((c) => c.guardado).length;
    const correctos = Object.values(conteos).filter((c) => c.guardado && c.correcto).length;
    const pct = contados > 0 ? Math.round((correctos / contados) * 1000) / 10 : null;
    return { contados, correctos, total: lotes.length, pct };
  }, [conteos, lotes]);

  return (
    <div style={styles.contenedor} className="contenedor-captura">
      <h1 style={styles.titulo}>Exactitud de Inventario — Conteo</h1>
      <p style={styles.subtitulo}>Captura las tarimas contadas físicamente; se suman solas y se comparan contra NetSuite.</p>

      <div style={styles.controles}>
        <label style={styles.label}>
          Fecha
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={styles.input} />
        </label>
        <label style={styles.label}>
          Tipo
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={styles.input}>
            <option value="PT">Producto Terminado</option>
            <option value="MP">Materia Prima / Empaque</option>
          </select>
        </label>
        <label style={styles.label}>
          Categoría {sugerencia[tipo] && <span style={styles.sugerenciaTag}>sugerido: {sugerencia[tipo]}</span>}
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)} style={styles.input}>
            <option value="" disabled>Elegir…</option>
            {CATEGORIAS[tipo].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label style={{ ...styles.label, justifyContent: 'flex-end' }}>
          &nbsp;
          <button
            onClick={() => setRevelarNetSuite((v) => !v)}
            style={{ ...styles.botonRevelar, ...(revelarNetSuite ? styles.botonRevelarActivo : {}) }}
          >
            {revelarNetSuite ? '🙈 Ocultar NetSuite' : '👁 Revelar NetSuite'}
          </button>
        </label>
      </div>

      {resumen.total > 0 && (
        <div style={styles.resumenBarra}>
          <span>{resumen.contados} / {resumen.total} lotes contados</span>
          {revelarNetSuite && resumen.pct !== null && (
            <span style={{ fontWeight: 700, color: resumen.pct === 100 ? '#3E7C5A' : '#B8862E' }}>
              Exactitud parcial: {resumen.pct}%
            </span>
          )}
        </div>
      )}

      {mensaje && <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      {cargando && <div style={styles.tarjeta}>Cargando lotes...</div>}

      {!cargando && categoria && lotes.length === 0 && (
        <div style={styles.tarjeta}>No hay lotes registrados para esta categoría. ¿Ya sincronizaste el inventario?</div>
      )}

      {!cargando && lotes.map((l) => {
        const clave = claveLote(l);
        const c = conteos[clave];
        const total = sumaTarimas(l);
        const bloqueado = l.sin_conversion || l.cantidad_netsuite === null;
        const diferencia = bloqueado ? null : total - parseFloat(l.cantidad_netsuite);
        const correctoPreview = diferencia === 0;
        const etiquetaUnidad = l.unidad === 'cajas' ? 'cajas' : 'piezas';

        return (
          <div key={clave} style={{
            ...styles.filaLote,
            ...(bloqueado ? styles.filaBloqueada : {}),
            ...(!bloqueado && revelarNetSuite && c?.guardado ? (c.correcto ? styles.filaCorrecta : styles.filaIncorrecta) : {}),
          }}>
            <div style={styles.loteInfo}>
              <div style={styles.loteArticulo}>{l.articulo}</div>
              <div style={styles.loteMeta}>
                {l.codigo_upc} · lote {l.numero_lote}
                {revelarNetSuite && !bloqueado && l.unidad === 'cajas' &&
                  ` · NetSuite: ${Number(l.cantidad_netsuite).toLocaleString('es-MX')} cajas (${Number(l.piezas_netsuite).toLocaleString('es-MX')} piezas)`}
                {revelarNetSuite && !bloqueado && l.unidad !== 'cajas' &&
                  ` · NetSuite: ${Number(l.cantidad_netsuite).toLocaleString('es-MX')} ${etiquetaUnidad}`}
              </div>
              {bloqueado && (
                <div style={styles.avisoBloqueado}>
                  Sin conversión de cajas capturada — <a href="/logistica/conversion-cajas" style={styles.link}>captúrala aquí</a> antes de auditar este lote.
                </div>
              )}
            </div>

            <div style={styles.tarimasWrap}>
              {getTarimas(l).map((v, idx) => (
                <div key={idx} style={styles.tarimaInputWrap}>
                  <input
                    type="number"
                    value={v}
                    placeholder="0"
                    disabled={bloqueado}
                    onChange={(e) => handleCambiarTarima(l, idx, e.target.value)}
                    style={styles.tarimaInput}
                  />
                  {getTarimas(l).length > 1 && (
                    <button onClick={() => handleQuitarTarima(l, idx)} style={styles.botonQuitar} title="Quitar tarima">×</button>
                  )}
                </div>
              ))}
              <button onClick={() => handleAgregarTarima(l)} disabled={bloqueado} style={styles.botonAgregarTarima}>+ tarima</button>
            </div>

            <div style={styles.loteResultado} className="loteResultado-mobil">
              <div style={styles.conteoRealTexto}>
                Conteo: <strong>{total.toLocaleString('es-MX')}</strong> {etiquetaUnidad}
                {!bloqueado && l.unidades_por_caja && (
                  <div style={styles.conteoPiezasEquiv}>≈ {(total * parseFloat(l.unidades_por_caja)).toLocaleString('es-MX')} piezas</div>
                )}
              </div>
              {revelarNetSuite && !bloqueado && (
                <div style={{ fontSize: '11px', color: correctoPreview ? '#3E7C5A' : '#B23A48', fontWeight: 700 }}>
                  {diferencia === 0 ? '✓ Coincide' : `Dif: ${diferencia > 0 ? '+' : ''}${diferencia}`}
                </div>
              )}
              <button onClick={() => handleGuardarLote(l)} disabled={bloqueado || guardandoLote === clave} style={styles.botonGuardar}>
                {guardandoLote === clave ? '...' : c?.guardado ? '✓ Guardado' : 'Guardar'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '920px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '2px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '18px' },
  controles: { display: 'flex', gap: '18px', flexWrap: 'wrap', marginBottom: '16px' },
  label: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: '#8A7690', fontWeight: 600, textTransform: 'uppercase' },
  input: { padding: '8px 10px', fontSize: '13px', border: '1px solid #D6A644', borderRadius: '6px', fontFamily: 'Inter, sans-serif', background: '#FFFDF7', minWidth: '170px' },
  sugerenciaTag: { color: '#6A2C75', fontWeight: 400, textTransform: 'none', marginLeft: '6px' },
  botonRevelar: { padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#6E4F80', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' },
  botonRevelarActivo: { background: '#6A2C75', color: '#fff', borderColor: '#6A2C75' },
  resumenBarra: { display: 'flex', justifyContent: 'space-between', background: '#fff', border: '1px solid #E6DDD2', borderRadius: '10px', padding: '10px 16px', fontSize: '13px', marginBottom: '14px' },
  mensaje: { fontSize: '13px', marginBottom: '14px', fontWeight: 600 },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)' },
  filaLote: { display: 'flex', alignItems: 'center', gap: '16px', background: '#fff', border: '1px solid #E6DDD2', borderRadius: '10px', padding: '12px 16px', marginBottom: '10px', flexWrap: 'wrap' },
  filaCorrecta: { borderColor: '#3E7C5A', background: '#F0F7F2' },
  filaIncorrecta: { borderColor: '#B23A48', background: '#FDF3F1' },
  filaBloqueada: { borderColor: '#D6A644', background: '#FBF6E9', opacity: 0.85 },
  avisoBloqueado: { fontSize: '10.5px', color: '#B8862E', marginTop: '4px', maxWidth: '260px' },
  link: { color: '#6A2C75', fontWeight: 600 },
  loteInfo: { flex: '1 1 220px', minWidth: '200px' },
  loteArticulo: { fontSize: '13px', fontWeight: 700, color: '#2A1830' },
  loteMeta: { fontSize: '11px', color: '#8A7690', marginTop: '2px' },
  tarimasWrap: { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', flex: '1 1 260px' },
  tarimaInputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  tarimaInput: { width: '64px', padding: '6px 8px', fontSize: '12px', border: '1px solid #D6A644', borderRadius: '6px', background: '#FFFDF7' },
  botonQuitar: { marginLeft: '2px', width: '18px', height: '18px', border: 'none', background: 'transparent', color: '#B23A48', cursor: 'pointer', fontSize: '14px', lineHeight: 1 },
  botonAgregarTarima: { padding: '6px 10px', fontSize: '11px', color: '#6E4F80', background: '#EFE9F3', border: '1px dashed #A98BB8', borderRadius: '6px', cursor: 'pointer' },
  loteResultado: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', minWidth: '110px' },
  conteoRealTexto: { fontSize: '12px', color: '#3A2E42' },
  conteoPiezasEquiv: { fontSize: '10.5px', color: '#8A7690', marginTop: '2px' },
  botonGuardar: { padding: '5px 12px', fontSize: '11px', fontWeight: 600, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '6px', cursor: 'pointer' },
};